import { useGetList, useRecordContext } from "ra-core";
import { ExternalLink, Mail, Phone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { BriefBanner, type BriefTheme } from "../contacts/BriefIABanner";

import type { Company, Contact } from "../types";

// Sprint 10.x — Brief IA company conforme maquette
// design/atomic-crm/Entreprises.html D1 + .ent-brief CSS.
// Variante company de contacts/BriefIABanner (BriefBanner) :
//  - thème vert FABRIK (au lieu de violet contact)
//  - skill brief_pre_company / table company_briefs
//  - action bar bottom : Appeler / Email / Lien (du premier contact rattaché)

const COMPANY_BRIEF_THEME: Partial<BriefTheme> = {
  container: "bg-fabrik-green-soft/40 border-fabrik-green/30",
  containerError: "bg-fabrik-red-soft border-fabrik-red/30",
  icon: "bg-fabrik-green-soft text-fabrik-accent-text",
  iconError: "bg-fabrik-red-soft text-fabrik-red",
  label: "text-fabrik-accent-text",
  labelError: "text-fabrik-red",
  staleBadge: "bg-fabrik-orange-soft text-fabrik-orange",
  muted: "text-fabrik-text-3",
  collapseHover: "hover:bg-fabrik-green-soft",
  skeleton: "bg-fabrik-green/20",
  marker: "marker:text-fabrik-green/70",
  link: "text-fabrik-accent-text hover:underline",
  code: undefined,
  em: "not-italic bg-fabrik-green-soft text-fabrik-accent-text px-1 py-0.5 rounded text-[12.5px]",
};

export function CompanyBriefIABanner() {
  const record = useRecordContext<Company>();
  // 1er contact de la company pour les boutons Appeler / Email
  const primaryContactQuery = useGetList<Contact>(
    "contacts_summary",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "last_seen", order: "DESC" },
      filter: { company_id: record?.id, "archived_at@is": null },
    },
    { enabled: !!record?.id },
  );
  if (!record) return null;
  const primaryContact = primaryContactQuery.data?.[0];
  const primaryEmail = primaryContact?.email_jsonb?.[0]?.email ?? null;
  const primaryPhone = primaryContact?.phone_jsonb?.[0]?.number ?? null;

  return (
    <BriefBanner
      resource="company_briefs"
      foreignKey="company_id"
      skillId="brief_pre_company"
      entityType="company"
      entityLabel={record.name || `Entreprise #${record.id}`}
      title="Brief IA · DeepSeek"
      testId="company-brief-ia-banner"
      collapseStorageKey="company-brief-ia-banner-collapsed"
      emptyTitle="Aucun brief IA généré pour cette entreprise"
      emptyDescription="Synthétise état commercial, multi-décideurs, signaux produit et propose la meilleure prochaine action."
      theme={COMPANY_BRIEF_THEME}
      extraActions={
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {primaryPhone && (
            <Button asChild size="sm" variant="outline" className="gap-1.5 h-7">
              <a href={`tel:${primaryPhone}`}>
                <Phone className="size-3" />
                Appeler maintenant
              </a>
            </Button>
          )}
          {primaryEmail && (
            <Button asChild size="sm" variant="outline" className="gap-1.5 h-7">
              <a href={`mailto:${primaryEmail}`}>
                <Mail className="size-3" />
                Préparer email
              </a>
            </Button>
          )}
          <Button asChild size="sm" variant="outline" className="gap-1.5 h-7">
            <a
              href={`${window.location.origin}/#/companies/${record.id}/show`}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink className="size-3" />
              Lien vers fiche
            </a>
          </Button>
        </div>
      }
    />
  );
}
